import { IBaseConfigModule } from 'library/ts/interfaces/config/IBaseConfigModule';
import { apiMetadataItemBlueprint, IAPIMetadataItem } from 'library/ts/interfaces/metadata/api/IAPIMetadataItem';
import { IProjectInfo } from 'library/ts/IProjectInfo';
import { AssetEmbedType } from 'library/ts/model/enums/content/AssetEmbedType';
import { MPXStatus } from 'library/ts/model/enums/mpx/MPXStatus';
import { IConveyorResult } from 'library/ts/utilities/conveyor';
import { TagValidationUtils } from 'tag/ts/smart_tag/operations/types/TagValidationUtils';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IConfig } from 'tag/ts/tag/interfaces/config/IConfig';
import { ITagConfig } from 'tag/ts/tag/interfaces/config/ITagConfig';
import { ISkinModeResolver } from 'tag/ts/tag/interfaces/model/ISkinModeResolver';
import { ITagOnceSettingsManager } from 'tag/ts/tag/interfaces/model/ITagOnceSettingsManager';
import { ITagModel } from 'tag/ts/tag/interfaces/mvc/ITagModel';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { IAdUtilities } from 'tag/ts/tag/interfaces/utilities/IAdUtilities';
import { ISiteUtilities } from 'tag/ts/tag/interfaces/utilities/ISiteUtilities';
import { ITagContentUtilities } from 'tag/ts/tag/interfaces/utilities/ITagContentUtilities';
import { DefaultTagConfig } from 'tag/ts/tag/model/config/DefaultTagConfig';
import { SkinModeResolver } from 'tag/ts/tag/model/config/SkinModeResolver';
import { CONVEYOR_KEY_API_VIDEO_LOAD_PARAMS_VO } from 'tag/ts/tag/model/enums/ConveyorKeys';
import { TagLoggerString } from 'tag/ts/tag/model/enums/TagLoggerString';
import { TagErrorCode } from 'tag/ts/tag/model/error/TagErrorCode';
import { TagOnceSettingsManager } from 'tag/ts/tag/model/TagOnceSettingsManager';
import { ConfigType } from 'tag/ts/tag/model/type/ConfigType';
import { SkinMode } from 'tag/ts/tag/model/type/SkinMode';
import { ParamsVO } from 'tag/ts/tag/model/vo/ParamsVO';
import { AdUtilities } from 'tag/ts/tag/utilities/AdUtilities';
import { ConfigUtilities } from 'tag/ts/tag/utilities/ConfigUtilities';
import { isMobileWebUIForced } from 'tag/ts/tag/utilities/MobileWebUIForceResolver';
import { SiteUtilities } from 'tag/ts/tag/utilities/SiteUtilities';
import { TagContentUtilities } from 'tag/ts/tag/utilities/TagContentUtilities';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Tsg Model
 */
export class TagModel extends AbstractTagClass implements ITagModel {
	// placeholder for loaded + processed + flattened params.config passed for page
	// Initialize with DefaultTagConfig to ensure plugins can load during construction
	#config: IConfig = { tag: DefaultTagConfig };

	readonly #contentUtilities: ITagContentUtilities;
	readonly #adUtilities: IAdUtilities;
	readonly #siteUtilities: ISiteUtilities;
	readonly #skinModeResolver: ISkinModeResolver;

	/**
	 * @description Placeholder for TagOnceSettingsManager class instance
	 * @type {ITagOnceSettingsManager}
	 * @private
	 */
	#onceSettingsManager: ITagOnceSettingsManager;

	/**
	 * @description Placeholder for TagOnceSettingsManager class instance
	 * @param {ITagAbstractClassArgument} argument
	 */
	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'TagModel'
			}
		);

		this.#adUtilities = new AdUtilities( { tag: argument.tag } );
		this.#contentUtilities = new TagContentUtilities( { tag: argument.tag } );
		this.#siteUtilities = new SiteUtilities( { tag: argument.tag } );
		this.#onceSettingsManager = new TagOnceSettingsManager( { tag: argument.tag } );
		this.#skinModeResolver = new SkinModeResolver( { tag: argument.tag } );

		// setup tag events handler
		this.tagEventsHandler(
			this.#onTagEvent,
			[
				TagEvent.ON_API_LOAD_VIDEO_REQUEST
			]
		);
	}

	/**
	 * @description Ad Utilities class reference
	 */
	get adUtilities() {
		return this.#adUtilities;
	}

	/**
	 * @description Content utilities for specific methods (i.e. DAI related etc.)
	 */
	get contentUtilities() {
		return this.#contentUtilities;
	}

	/**
	 * @description Returns instance of SiteUtilities class
	 */
	get siteUtilities() {
		return this.#siteUtilities;
	}

	/**
	 * @description Resolved skin engine for this session — see SkinModeResolver
	 */
	get skinMode(): SkinMode {
		return this.#skinModeResolver.resolve();
	}

	/**
	 * @description Getter for the `onceSettingsManager` property.
	 * Provides access to the instance of `ITagOnceSettingsManager` used to manage
	 * settings for operations that should occur only once.
	 * @returns {ITagOnceSettingsManager} The instance of `ITagOnceSettingsManager`.
	 */
	get onceSettingsManager(): ITagOnceSettingsManager {
		return this.#onceSettingsManager;
	}

	/**
	 * @description Returns params.config loaded + processed + flattened
	 * Merges DefaultTagConfig with runtime config from params on first access
	 * @return {IConfig}
	 */
	get config(): IConfig {
		// If config hasn't been loaded yet and we have runtime config, merge it with DefaultTagConfig
		// This ensures plugins can see the merged config during TagModel construction
		if ( this.#config?.tag === DefaultTagConfig ) {
			const runtimeTagConfig = this?.tag?.params?.TAG?.config?.tag;
			const utils = window?.SmartTag?.utilities;

			// Merge tag config with runtime config from test harness
			if ( runtimeTagConfig && Array.isArray( runtimeTagConfig ) && runtimeTagConfig.length > 0 ) {
				let mergedTagConfig = { ...DefaultTagConfig };
				runtimeTagConfig.forEach( ( configItem: string | { [ key: string ]: IBaseConfigModule } ) => {
					if ( utils?.Type?.isObject( configItem ) ) {
						mergedTagConfig = utils.DeepMerge.deepmerge( mergedTagConfig, configItem );
					}
				} );
				this.#config.tag = mergedTagConfig;
			}
			// Note: Skin config is handled separately when Skin is initialized via loadConfigs(ConfigType.SKIN)
		}
		return this.#config;
	}

	/**
	 * @description Returns API metadata for current video for this Tag
	 * @returns {IAPIMetadataItem}
	 */
	get apiMetadata(): IAPIMetadataItem {
		if (
			this.utils.Type.isArray( this?.params?.CONTENT?.apiMetadata ) &&
			this.params.CONTENT.apiMetadata.length > 0
		) {
			return this.params.CONTENT.apiMetadata[ 0 ];
		} else {
			return apiMetadataItemBlueprint();
		}
	}

	/**
	 * @description Load configs
	 */

	/**
	 * @description Load configs of particular type
	 * @param {string} configType
	 * @param {( config: IConfig ) => void} onSuccessCallback
	 * @param {( error: any ) => void} onFailureCallback
	 * @param {any} defaultConfig
	 */
	loadConfigs = (
		configType: ConfigType,
		onSuccessCallback: ( config: IConfig ) => void,
		onFailureCallback: ( error: any ) => void,
		defaultConfig: any = undefined
	): void => {
		this?.logger?.log( 'loadConfigs:', 'configType:', configType, 'defaultConfig:', defaultConfig );

		const onSuccess = ( config: IConfig ): void => {
			this?.logger?.log( 'loadConfigs: Success:', config );
			this.#config = this.utils.DeepMerge.deepmerge( this.#config, config ); // store processed config in local read only property
			// execute callback
			if ( this.utils.Type.isFunction( onSuccessCallback ) ) {
				onSuccessCallback( this.config );
			}
			if ( configType === ConfigType.TAG ) {
				// inform Tag application if Tag config load was requested
				this.#informApplication( TagEvent.ON_MODEL_READY );
			}
		};

		const onFailure = (): void => {
			this?.logger?.warn( 'loadConfigs: Failure:' );
			if ( configType === ConfigType.TAG ) {
				// inform Tag application if Tag config load was requested
				this.#informApplication( TagEvent.ON_MODEL_FAILURE );
			}
		};

		// if params.config is not defined create empty config
		if ( this.utils.Type.isObject( this.params.TAG.config ) === false ) {
			this.params.TAG.config = { tag: [], skin: [] };
		} else {
			Object.keys( ConfigType ).forEach(
				( key ) => {
					if ( this.utils.Type.isArray( this.params.TAG.config[ ConfigType[ key ] ] ) === false ) {
						this.params.TAG.config[ ConfigType[ key ] ] = [];
					}
				}
			);
		}

		/**
		 * @description Process config
		 * @return {(string | {[p: string]: IBaseConfigModule})[]}
		 * @param config
		 */
		const processConfig = ( config: ( string | { [ p: string ]: IBaseConfigModule } )[] ): ( string | { [ p: string ]: IBaseConfigModule } )[] => {
			// decorates URL with ssl and tokens
			const decorateURL = ( url: string ): string => {
				url = this.utils.URL.addCacheToken( url, this.runtimeVO.debug.cacheBust );
				if ( this.runtimeVO.top.ssl ) {
					url = this.utils.URL.toHTTPS( url );
				}
				return url;
			};

			// loop over config items
			for ( let i = 0; i < config.length; i++ ) {
				const item = config[ i ];

				if ( this.utils.Type.isString( item ) && this.utils.URL.valid( <string>item ) ) {
					let url = <string>item; // cast item to string
					url = decorateURL( url ); // decorate URL
					config[ i ] = url; // assign processed URL back to config...
				}
			}

			return config;
		};

		/**
		 * @description Processes default tag config before our deeper processing
		 * Main reason, we want to set and normalize some default imports URLs
		 * @param {ITagConfig} defaultTagConfig
		 * @returns {ITagConfig}
		 */
		const processDefaultTagConfig = ( defaultTagConfig: ITagConfig ): ITagConfig => {
			const projectInfo: IProjectInfo = window.PlayerServicesProjectInfo;
			const originURL = this.utils.URL.getOrigin( this.runtimeVO.url.root );

			// default shaka URLs
			const shakaURL =
				`${originURL}${projectInfo.dependencies.bundle.tag.dir.value}/` +
				`${projectInfo.dependencies.bundle.tag.player.shaka.dir.output.value}/` +
				`${projectInfo.dependencies.bundle.tag.player.shaka.filename.value}`;

			defaultTagConfig.Imports.params.low_level.shaka.url.regular = shakaURL;

			// default hlsjs URLs
			const hlsjsURL =
				`${originURL}${projectInfo.dependencies.bundle.tag.dir.value}/` +
				`${projectInfo.dependencies.bundle.tag.player.hlsjs.dir.output.value}/` +
				`${projectInfo.dependencies.bundle.tag.player.hlsjs.filename.value}`;

			defaultTagConfig.Imports.params.low_level.hlsjs.url.regular = hlsjsURL;

			// default omsdk URLs — resolved from Smart Tag's own origin so they work
			// across all three CDN domains (paramountplus.com, cbs.com, pluto.tv) without hardcoding
			const omSdkDir =
				`${originURL}${projectInfo.dependencies.bundle.tag.dir.value}/` +
				`${projectInfo.dependencies.bundle.tag.player.omsdk.dir.output.value}/`;
			const omSdkServiceURL =
				`${omSdkDir}${projectInfo.dependencies.bundle.tag.player.omsdk.filename.service.value}`;
			const omSdkSessionClientURL =
				`${omSdkDir}${projectInfo.dependencies.bundle.tag.player.omsdk.filename.sessionClient.value}`;

			// only set defaults when the page hasn't already supplied a value —
			// allows overriding the URLs (e.g. with the validation script for QA)
			// or partnerVersion without needing to touch TagConfigOmSdk directly.
			// serviceUrl + sessionClientUrl feed Blackbird's two-file loader; loaderUrl
			// stays defaulted to the Service for back-compat (legacy concatenated path).
			// Guard: don't fill serviceUrl/sessionClientUrl when a consumer has explicitly
			// supplied loaderUrl — Blackbird gives the two-file path precedence, which
			// would silently ignore their loaderUrl override.
			const omSdkParams = defaultTagConfig.OmSdk?.params;
			if ( omSdkParams ) {
				const hasExplicitLoaderUrl = !!omSdkParams.loaderUrl;
				if ( !omSdkParams.serviceUrl && !hasExplicitLoaderUrl ) {
					omSdkParams.serviceUrl = omSdkServiceURL;
				}
				if ( !omSdkParams.sessionClientUrl && !hasExplicitLoaderUrl ) {
					omSdkParams.sessionClientUrl = omSdkSessionClientURL;
				}
				if ( !omSdkParams.loaderUrl ) {
					omSdkParams.loaderUrl = omSdkServiceURL;
				}
				if ( !omSdkParams.partnerVersion ) {
					omSdkParams.partnerVersion = projectInfo.release_version;
				}
			}

			// default playerVersion — resolved from Smart Tag release version if not set by the page;
			// allows override without touching TagConfigPal directly
			const palParams = defaultTagConfig.Pal?.params;
			if ( palParams && !palParams.playerVersion ) {
				palParams.playerVersion = projectInfo.release_version;
			}

			// Mobile Web UI plugin enablement — a per-partner avia-js-ui customization layer,
			// loaded whenever avia-js-ui itself is loading on an actually-mobile device. Not
			// partner-gated: the plugin instantiates a per-partner "customizer" internally (see
			// MobileWebUI.ts) — Pluto gets a concrete implementation, other partners a no-op
			// placeholder until they need their own. !isDesktop() (not embedType) mirrors
			// SkinModeResolver's own device-capability check, so this can't fire for the
			// SKIN.defaultSkin-forced-on-real-desktop-hardware edge case, where skinMode is
			// AVIA_UI but the device genuinely isn't mobile — except when ?forceMobileWebUI=1
			// deliberately overrides that guard for dev/QA testing on real desktop hardware
			// (combine with ?skinMode=avia to get skinMode === AVIA_UI there too).
			const mobileWebUIParams = defaultTagConfig.MobileWebUI;
			if ( mobileWebUIParams ) {
				const isMobileWebUIEnabled = this.skinMode === SkinMode.AVIA_UI
					&& ( !this.isDesktop() || isMobileWebUIForced( this.utils, this.runtimeVO ) );

				mobileWebUIParams.enabled = isMobileWebUIEnabled;
				mobileWebUIParams.params.import.app.enabled = isMobileWebUIEnabled;
				mobileWebUIParams.params.import.css.enabled = isMobileWebUIEnabled;
			}

			return defaultTagConfig;
		};

		// filter by config type load request
		switch ( configType ) {
			case ConfigType.TAG:
				// set default Tag config if it was passed in
				if ( this.utils.Type.isDefined( defaultConfig ) ) {
					this.#config.tag = processDefaultTagConfig( defaultConfig as ITagConfig );
				}
				// process config module before loading
				this.params.TAG.config.tag = processConfig( this.params.TAG.config.tag );
				this?.logger?.log( 'loadConfigs:', configType, this.params.TAG.config.tag );
				// start loading
				ConfigUtilities.getConfigs( this.uuid, { tag: this.params.TAG.config.tag }, onSuccess, onFailure );
				break;
			case ConfigType.SKIN:
				// set default Skin config if it was passed in
				if ( this.utils.Type.isDefined( defaultConfig ) ) {
					this.#config.skin = defaultConfig;
				}
				// process config module before loading
				this.params.TAG.config.skin = processConfig( this.params.TAG.config.skin );
				this?.logger?.log( 'loadConfigs:', configType, this.params.TAG.config.skin );
				ConfigUtilities.getConfigs( this.uuid, { skin: this.params.TAG.config.skin }, onSuccess, onFailure );
				break;
		}
	};

	/**
	 * @description Inform application by dispatching event
	 * @param {TagEvent} eventType
	 */
	#informApplication = ( eventType: TagEvent ): void => {
		// dispatch application event
		const eventData = this.getTagEventDataVO();
		eventData.paramsVO = this.params;
		const tagEventVO = this.getTagEventVO( eventType, this.tag, eventData );
		this.dispatchTagEvent( tagEventVO );

		if ( eventType === TagEvent.ON_METADATA_READY ) {
			// inform tag application
			this.dispatchTagEvent(
				new TagEventVO(
					TagEvent.ON_MODEL_READY,
					this.tag,
					undefined,
					this,
					this.name
				)
			);
		}
	};

	/**
	 * @description Checks for existence of video metadata + decides if we should request metadata from CAN VPS service or proceed with playback
	 */
	#checkMetadata = (): void => {
		this?.logger?.log( 'checkMetadata', this.params.CONTENT.apiMetadata );
		const assetEmbedType = this.contentUtilities.assetEmbedType( this.params );

		// INFO: assetURL embed
		if ( assetEmbedType === AssetEmbedType.ASSET_URL ) {
			this.#informApplication( TagEvent.ON_METADATA_READY ); // inform application
			return; // exit without requesting MPX metadata from CAN...
		}

		// INFO: Embed by MPX PID
		if ( assetEmbedType === AssetEmbedType.PID ) {
			// MPX pid
		}

		// INFO: Embed by MPX Content ID
		if ( assetEmbedType === AssetEmbedType.CID ) {
			// MPX content id
		}

		/**
		 * @description Process API Metadata
		 * @param {IAPIMetadataItem[]} metadataArray
		 * @returns {IAPIMetadataItem[]}
		 */
		const processAPIMetadata = ( metadataArray: IAPIMetadataItem[] ): IAPIMetadataItem[] => {
			if ( this.utils.Type.isArray( metadataArray ) && this.utils.Type.isEmpty( metadataArray ) === false ) {
				metadataArray.forEach( ( apiMetadataItem ) => {
					if ( this.utils.Type.isEmpty( apiMetadataItem ) === false ) {
						Object.keys( apiMetadataItemBlueprint() ).forEach(
							( key ) => {
								// filter per metadata property name + process (initialize) values if needed
								switch ( key ) {
									case 'availableForProfileTypes':
									case 'downloadCountrySet':
									case 'premiumFeatures':
									case 'videoProperties':
										if ( apiMetadataItem[ key ] === undefined ) {
											apiMetadataItem[ key ] = [];
										}
										break;
								}
							}
						);
						// duplicate apiMetadata.title as apiMetadata.videoTitle
						apiMetadataItem.videoTitle = apiMetadataItem.title;
					}
				} );
			}
			return metadataArray;
		};

		/**
		 * @description Inform application about API metadata readiness or stop execution is video is not allowed to play from MPX
		 * @param {IAPIMetadataItem[]} apiMetadata
		 */
		const done = ( apiMetadata: IAPIMetadataItem[] = this.params.CONTENT.apiMetadata ): void => {
			// check if video playback is allowed by the API
			switch ( apiMetadata[ 0 ].status === MPXStatus.AVAILABLE ) {
				case true:
					apiMetadata = processAPIMetadata( apiMetadata );
					this.#informApplication( TagEvent.ON_METADATA_READY ); // inform application
					break;
				case false:
					// tag fatal error event dispatched internally
					this.dispatchTagEvent(
						this.getTagEventVO( TagEvent.ON_FATAL_ERROR, this.tag, {
							fatalError: {
								code: TagErrorCode.METADATA_SERVICE_NOT_ALLOWED,
								msg: {
									original: undefined,
									custom: this.tag.errorManager.createErrorMessageByCode( TagErrorCode.METADATA_SERVICE_NOT_ALLOWED )
								},
								error: { isServiceAllowed: false }
							}
						} )
					);
					break;
			}
		};

		// api metadata validity check
		const apiMetadataValid: boolean =
			this.utils.Type.isArray( this?.params?.CONTENT?.apiMetadata )
			&&
			this.params.CONTENT.apiMetadata.length > 0
			&&
			!this.utils.Type.isEmpty( this.params.CONTENT.apiMetadata[ 0 ] );

		// check if we have API metadata
		switch ( apiMetadataValid ) {
			case true: // API metadata passed by page
				done(); // inform application without requesting metadata from API end point
				break;
			case false:
				// TODO: Make call to API?
				this?.logger?.warn( 'checkMetadata:', 'WARNING: apiMetadata is not set in params! Stop execution.' );
				break;
		}
	};

	#processParams = async (): Promise<void> => {
		// Create a copy of params to be processed safely
		let processedParams = this.utils.DeepMerge.deepmerge( {}, this.params );

		const paramsConveyor = this.utils.Conveyor( CONVEYOR_KEY_API_VIDEO_LOAD_PARAMS_VO );

		let processingError;

		const handleError = ( error: IConveyorResult | Error ) => {
			processingError = error;
			this?.logger?.warn( 'processParams:', error );
			return false; // continue processing
		};

		try {
			// process paramsVO through Conveyor
			processedParams = await paramsConveyor.process(
				processedParams,
				{
					onError: handleError
				}
			);
		} catch ( error ) {
			handleError( error );
		}

		if (
			this.utils.Type.isObject( processedParams )
			&& this.utils.Type.isUndefined( processingError )
		) {
			this?.logger?.log( 'paramsVO processed:', 'processParams:', processedParams );
			this.params = processedParams; // update params
		} else {
			this?.logger?.warn( 'Processing params was not successful.', 'processedParams:', processedParams, 'processingError:', processingError );
		}

		// check metadata if not using stitcher, else dispatch ready event
		if ( this.hasStitcherParams() ) {
			this.#informApplication( TagEvent.ON_METADATA_READY );
		} else {
			this.#checkMetadata();
		}
	};

	/**
	 * @description Checks params for playability
	 * @param {ParamsVO} paramsVO
	 */
	#checkParams = ( paramsVO: ParamsVO = {} as ParamsVO ): void => {
		const LOG_PREFIX = 'checkParams:';
		this?.logger?.log( LOG_PREFIX, paramsVO );

		// sanity check
		if ( this.utils.Type.isEmpty( paramsVO ) ) paramsVO = {} as ParamsVO;

		// check if we have paramsVO object
		switch ( this.utils.Type.isObject( paramsVO ) ) {
			case true: // paramsVO is an object
				// Deepmerge union-merges arrays, so stale stitcher items/episodes/channels from a prior
				// load would survive into the next video. Clear them before merging so incoming values win.
				if ( this.params?.STITCHER && ( paramsVO as IParamsVO )?.STITCHER ) {
					delete this.params.STITCHER.items;
					delete this.params.STITCHER.episodes;
					delete this.params.STITCHER.channels;

					// coppa is a per-load kids-content signal (PalPlugin suppression, PLBCK-8475). TN Web
					// only sends it for kids content and omits it for adult content, so deepmerge would
					// otherwise carry a stale coppa='1' into an adult reload. Clear unconditionally, same
					// idiom as items/episodes/channels — other privacy fields (usPrivacy, gdpr, gdprConsent)
					// are session consent signals that may legitimately persist unchanged.
					if ( this.params.STITCHER.privacy ) {
						delete this.params.STITCHER.privacy.coppa;
					}
				}

				// callSignId is per-stream for eligible domestic live Local DAI requests. Clear any
				// prior value before merge so omitting it on a later load does not leak stale station
				// data into ad calls for the next, unrelated stream.
				if ( this.params?.AD?.callSignId !== undefined ) {
					this.params.AD.callSignId = undefined;
				}

				// FW.FMS is per-stream ad metadata. Clear it only when the incoming load
				// supplies a replacement: deepmerge would otherwise union stale keys into
				// the new stream (PLSTVVID-6240), and unconditional clearing wipes FMS on
				// in-session midroll loads where the host omits it (CBSVIDEO-9205, desktop CLID3).
				if (
					this.params?.AD?.FW?.FMS !== undefined
					&& ( paramsVO as IParamsVO )?.AD?.FW?.FMS !== undefined
				) {
					delete this.params.AD.FW.FMS;
				}

				this.params = this.utils.DeepMerge.deepmerge( this.params, paramsVO ); // merge params
				this.params = this.tag.tagUtilities.normalizeParams( this.params ); // apply defaults on merged params (e.g. SKIN.cp.enabled)
				this.params = this.tag.tagUtilities.namespaceConverter( this.params ); // convert namespaces from string to object
				this?.logger?.log( LOG_PREFIX, 'params post-merge:', this.params );

				// For stitcher playback, validate required stitcher params and bypass embed-type check.
				if ( this.hasStitcherParams() ) {
					this.logger?.log( LOG_PREFIX, 'hasStitcherParams===true', this.params );
					if (
						this.params.STITCHER.format &&
						this.params.STITCHER.id &&
						this.params.STITCHER.token &&
						this.params.STITCHER.type
					) {
						this.#processParams();
					} else {
						this?.logger?.warn( LOG_PREFIX, 'ERROR: detected STITCHER params, but some params are missing' );
					}
					break;
				}

				// Delegate embed type validation to the shared utility — single source of truth
				// for "what is a valid/playable asset identifier" across all entry points.
				const isParamsValid = TagValidationUtils.isValidEmbedType(
					this.contentUtilities.assetEmbedType( this.params )
				);
				// filter by params validity
				switch ( isParamsValid.isValid ) {
					case true: // params are valid
						this.#processParams(); // process params
						break;
					case false: // params are not valid, can not continue
						this?.logger?.warn( LOG_PREFIX, isParamsValid.error );
						break;
				}
				break;
			case false: // paramsVO is not an object
				// TODO: ParamsVO is not an object => bail with error?
				this?.logger?.warn( LOG_PREFIX, 'ERROR: params is not an object!' );
				break;
		}
	};

	/**
	 * @description Tag application event handler
	 * @param {TagEventVO} event
	 */
	#onTagEvent = ( event: TagEventVO ): void => {
		// filter by event type
		switch ( event?.type ) {
			case TagEvent.ON_API_LOAD_VIDEO_REQUEST:
				this?.logger?.log( TagLoggerString.ON_TAG_EVENT, event?.type, event );
				this.#checkParams( event?.data?.paramsVO ); // check incoming parameters
				break;
		}
	};
}